import classNames from 'classnames';
import { useId } from 'react';
import { isStillPlaying, opponentName } from '../../../functions';
import { IVisibleGameState, IVisiblePlay } from '../../../Models/StateModels';
import Avatar, { CrownIcon } from '../Avatar/Avatar';
import { Ordinal } from '../Leaderboard/Leaderboard';
import { describePlay } from '../PlayHistory/PlayHistory';
import {
  BUTTON_TEXT_CLASS_NAME,
  DIALOG_CLASS_NAME,
  OVERLAY_CLASS_NAME,
  PRIMARY_BUTTON_CLASS_NAME,
} from '../StartScreen/styles';

interface IGameOverProps {
  visibleGameState: IVisibleGameState;
  playHistory: IVisiblePlay[];
  leaveGame: () => Promise<void>;
}

function standings({ player, opponents }: IVisibleGameState) {
  const players = [
    { ...player, isYou: true },
    ...opponents.map((opponent) => ({
      ...opponent,
      name: opponentName(opponent),
      isYou: false,
    })),
  ];
  const finished = players
    .filter((p) => p.place != null && !p.forfeited)
    .sort((a, b) => (a.place ?? 0) - (b.place ?? 0));
  const unfinished = players.filter(isStillPlaying);
  const forfeited = players.filter((p) => p.forfeited);

  return [
    ...finished.map((p, i) => ({ player: p, rank: i + 1, isTied: false })),
    ...unfinished.map((p) => ({
      player: p,
      rank: finished.length + 1,
      isTied: unfinished.length > 1,
    })),
    ...forfeited.map((p) => ({ player: p, rank: undefined, isTied: false })),
  ];
}

export default function GameOver({
  visibleGameState,
  playHistory,
  leaveGame,
}: IGameOverProps) {
  const titleId = useId();
  const rows = standings(visibleGameState);
  const winner = rows.find(({ rank, isTied }) => rank === 1 && !isTied)?.player;
  const lastPlay = playHistory[playHistory.length - 1];

  return (
    <div className={classNames(OVERLAY_CLASS_NAME, 'z-50')}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={classNames(DIALOG_CLASS_NAME, 'items-center')}
      >
        <span
          aria-hidden="true"
          className="flex size-[56px] shrink-0 items-center justify-center rounded-full bg-accent-fill text-accent-on"
        >
          <CrownIcon size={28} />
        </span>
        <h2 id={titleId} className="mt-[12px] text-[20px] font-bold">
          Game over
        </h2>
        {winner && (
          <span className="mt-[2px] text-[14px] font-medium text-ink-muted">
            {winner.isYou ? 'You won' : `${winner.name} won`}
          </span>
        )}
        {lastPlay && (
          <p className="mt-[12px] w-full rounded-[12px] bg-control px-[12px] py-[8px] text-center text-[14px] font-medium">
            <span className="block text-[11px] font-bold tracking-wide text-ink-muted uppercase">
              Last play
            </span>
            {describePlay(
              lastPlay,
              visibleGameState.player,
              visibleGameState.opponents
            )}
          </p>
        )}
        <ol
          aria-label="Results"
          className="mt-[16px] min-h-0 w-full divide-y divide-hairline overflow-y-auto"
        >
          {rows.map(({ player, rank, isTied }) => (
            <li
              key={player.playerIndex}
              className="flex h-[52px] items-center gap-[12px]"
            >
              <span className="flex w-[32px] shrink-0 flex-col items-center text-[13px] font-bold text-ink-muted">
                {rank != null && <Ordinal rank={rank} />}
                {isTied && (
                  <span className="-mt-[2px] text-[10px] font-semibold">
                    tied
                  </span>
                )}
              </span>
              <Avatar
                name={player.name}
                colorIndex={player.playerIndex}
                className={classNames('size-[32px] text-[14px]', {
                  'opacity-40': player.forfeited,
                })}
              />
              <span className="min-w-0 truncate text-[15px] font-semibold">
                {player.name}
              </span>
              {player.isYou && (
                <span className="shrink-0 rounded-full bg-control px-[8px] py-[2px] text-[11px] font-bold text-ink-muted">
                  You
                </span>
              )}
              {player.forfeited && (
                <span className="ml-auto shrink-0 text-[12px] font-medium text-ink-muted">
                  Left
                </span>
              )}
            </li>
          ))}
        </ol>
        <button
          autoFocus
          onClick={leaveGame}
          className={classNames(PRIMARY_BUTTON_CLASS_NAME, 'mt-[16px] w-full')}
        >
          <span className={BUTTON_TEXT_CLASS_NAME}>Leave game</span>
        </button>
      </div>
    </div>
  );
}
